import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { TagModule } from 'primeng/tag';
import { SplitButtonModule } from 'primeng/splitbutton';
import { WorkflowApproval } from './workflow-apporval.model'; // Import the new model
import { MenuItem, MessageService } from 'primeng/api';
import { AppUser } from '../../users/user.model';
import { WorkflowApprovalService } from './workflow-approval.service';
import { AuthorizationService } from '../../services/authorization.service';
import { ToastModule } from 'primeng/toast';
import { TextareaModule } from 'primeng/textarea';
import { FloatLabelModule } from 'primeng/floatlabel';
import { FormsModule } from '@angular/forms';
import { HttpParams } from '@angular/common/http';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { of, throwError } from 'rxjs';
import { AppDatePipe } from '../../pipes/app-date.pipe';

@Component({
  selector: 'app-approval-card',
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    ButtonModule,
    AvatarModule,
    TagModule,
    SplitButtonModule,
    ToastModule,
    TextareaModule,
    FormsModule,
    FloatLabelModule,
    AppDatePipe
],
  providers: [DatePipe],
  templateUrl: './approval-card.component.html',
  styleUrls: ['./approval-card.component.css'],
})
export class ApprovalCardComponent implements OnInit {
  @Input() invokedFromChat: boolean = true;
  @Input() businessObjectId!: number;
  @Input() businessObjectDescription: string = 'N/A';
  @Input() workflowApprovals: WorkflowApproval[] = [];
  @Input() currentUser: AppUser | null = null;
  @Input() approvalContext: {
    customerId?: number;
    businessId?: number;
    businessUnitId?: number;
    branchId?: number;
    brandId?: number;
    supplierId?: number;
  } | null = null;

  @Output() openDetails = new EventEmitter<WorkflowApproval>();
  @Output() statusChange = new EventEmitter<WorkflowApproval>();

  successStatuses: MenuItem[] | undefined;
  failedStatuses: MenuItem[] | undefined;

  lastWorkflowApproved: WorkflowApproval | undefined;
  successWorkflowApprovals: WorkflowApproval[] = [];
  rejectedWorkflowApprovals: WorkflowApproval[] = [];

  isAuthorizedToAct: boolean = false;
  isProcessing: boolean = false;
  actionComments: string = '';

  constructor(
    private workflowApprovalService: WorkflowApprovalService,
    private datePipe: DatePipe,
    private messageService: MessageService,
    private authorizationService: AuthorizationService,
  ) { }

  ngOnInit(): void {
    this.processApprovals();
    this.checkAuthorization();
  }

  processApprovals() {
    this.workflowApprovals = this.workflowApprovals.filter(
      (approval) =>
        approval.isManualAction &&
        approval.workflowStatus?.statusName != 'End' &&
        approval.workflowStatus?.statusName != 'Start' &&
        (approval.isCurrentPending ||
          approval.actionTaken === 'APPROVED' ||
          approval.actionTaken === 'REJECTED'),
    );
    this.workflowApprovals.sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
    this.lastWorkflowApproved = this.workflowApprovals
      .filter((approval) => approval.actionTaken !== 'PENDING')
      .sort((a, b) => (b.id ?? 0) - (a.id ?? 0))[0];
    this.successWorkflowApprovals = this.workflowApprovals.filter(
      (approval) => approval.isSuccessAction && approval.isCurrentPending,
    );
    if (this.successWorkflowApprovals.length > 1) {
      this.successStatuses = this.successWorkflowApprovals.map((approval) => {
        return {
          label: approval.workflowStatus?.statusName || 'N/A',
          icon: 'pi pi-check',
          command: () => this.onActionTaken(approval, 'APPROVED'),
        };
      });
    }
    this.rejectedWorkflowApprovals = this.workflowApprovals.filter(
      (approval) => !approval.isSuccessAction && approval.isCurrentPending,
    );
    if (this.rejectedWorkflowApprovals.length > 1) {
      this.failedStatuses = this.rejectedWorkflowApprovals.map((approval) => {
        return {
          label: approval.workflowStatus?.statusName || 'N/A',
          icon: 'pi pi-times',
          command: () => this.onActionTaken(approval, 'REJECTED'),
        };
      });
    }
    this.checkAuthorization();
  }

  onOpenDetails(): void {
    this.openDetails.emit(this.lastWorkflowApproved);
  }

  onActionTaken(approval: WorkflowApproval, actionTaken: string): void {
    const approvalId = approval?.id ?? 0;
    if (approvalId === 0) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Invalid approval record.' });
      return;
    }

    if (actionTaken === 'REJECTED' && !this.actionComments) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Comments are required for rejection.' });
      return;
    }

    this.isProcessing = true;

    this.workflowApprovalService.recordAction(approvalId, { actionTaken: actionTaken, comments: this.actionComments })
      .pipe(
        tap((updatedApproval) => {
          this.lastWorkflowApproved = updatedApproval;
          console.log('[ApprovalCard] recordAction successful:', updatedApproval);
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Action recorded successfully.' });

          try {
            this.statusChange.emit(updatedApproval);
          } catch (error) {
            console.error('[ApprovalCard] Error in statusChange emit:', error);
          }

          const index = this.workflowApprovals.findIndex(app => app.id === updatedApproval.id);
          if (index !== -1) {
            this.workflowApprovals[index] = updatedApproval;
          }

          this.actionComments = '';
        }),
        switchMap(() => {
          return this.workflowApprovalService.getAllHistory(new HttpParams({
            fromObject: {
              resourceTypeId: approval.resourceType?.id ?? 0,
              businessObjectId: this.businessObjectId ?? approval.businessObjectId ?? 0
            }
          }));
        }),
        finalize(() => {
          this.isProcessing = false;
        }),
        catchError((err) => {
          console.error('[ApprovalCard] Error in workflow action chain:', err);
          if (err.error?.message?.includes('Access Denied')) {
            this.messageService.add({ severity: 'error', summary: 'Unauthorized', detail: 'You are not authorized to perform this action.' });
          } else {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: err.error?.message || 'Failed to complete workflow action.' });
          }

          return throwError(() => err);
        })
      )
      .subscribe({
        next: (historyResponse) => {
          this.workflowApprovals = historyResponse.content;
          this.processApprovals();
          console.log('[ApprovalCard] Workflow history refreshed.');
        }
      });
  }

  private checkAuthorization(): void {
    this.isAuthorizedToAct = this.authorizationService.isAuthorizedToAct(
      this.currentUser,
      this.workflowApprovals,
      this.approvalContext,
    );
  }

  isLineActive(index: number): boolean {
    return this.workflowApprovals[index].isCurrentPending === true;
  }

  getStatusSeverity(approval: WorkflowApproval | undefined): string {
    if (approval?.actionTaken === 'APPROVED' || approval?.isSuccessAction) {
      return 'success';
    }
    if (approval?.actionTaken === 'REJECTED' || !approval?.isSuccessAction) {
      return 'danger';
    }
    if (approval.isCurrentPending) {
      return 'warn';
    }

    // The filtering in `processApprovals` ensures that for any approval in the list,
    // one of the above conditions (APPROVED, REJECTED, or isCurrentPending) is true.
    // 'info' serves as a safe fallback for any unexpected cases.
    return 'info';
  }
}
